// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Engine Review panel: thin wrappers around the src/engine/review-model
// helpers, the per-metric lesson HTML, and the four section renderers
// (triage summary, review list, evidence panel, supporting checks) +
// the timeline-evidence selection handler.
//
// Exposed as a factory because the wrappers need app.js state
// (selectedFlight) and many helpers (detectionWindowsForHealthKey,
// reviewMonitorEnabled, etc.) that live in app.js or in factory
// destructures earlier/later in the init sequence.

import {
  engineFindingSnippet as engineFindingSnippetModel,
  engineForSummaryScope as engineForSummaryScopeModel,
  engineMetricCylinder as engineMetricCylinderModel,
  engineMetricLesson as engineMetricLessonModel,
  engineMetricRows as engineMetricRowsModel,
  engineReviewActionList as engineReviewActionListModel,
  engineReviewItems as engineReviewItemsModel,
  engineSeverityRank as engineSeverityRankModel,
  engineTriageStatus as engineTriageStatusModel,
  engineWindowLabel as engineWindowLabelModel,
  metricMatchesEngineScope as metricMatchesEngineScopeModel,
  scopedSummaryFinding as scopedSummaryFindingModel,
} from "../engine/review-model.js";

export function createEngineReviewRenderer({
  escapeHtml,
  compactHealthNote,
  currentEngineDescriptors,
  engineSourceNumbers,
  detectionWindowsForHealthKey,
  findingDisplayClass,
  findingDisplayLabel,
  findingForMetric,
  findingMetaMarkup,
  hasCarbTempChannel,
  healthMetricScope,
  reviewMonitorEnabled,
  scoreCardStateFromClass,
  getSelectedFlight,
}) {
  function engineReviewModelContext() {
    return {
      detectionWindowsForHealthKey,
      engineDescriptors: currentEngineDescriptors,
      engineSourceNumbers,
      findingDisplayClass,
      findingForMetric,
      hasCarbTempChannel,
      healthMetricScope,
      reviewMonitorEnabled: (key) => reviewMonitorEnabled(key, getSelectedFlight()?.aircraft),
      scoreCardStateFromClass,
    };
  }

  function engineMetricRows(summary) {
    return engineMetricRowsModel(summary, engineReviewModelContext());
  }

  function engineForSummaryScope(summary) {
    return engineForSummaryScopeModel(summary, engineReviewModelContext());
  }

  function metricMatchesEngineScope(key, metric, engine) {
    return metricMatchesEngineScopeModel(key, metric, engine, engineReviewModelContext());
  }

  function scopedSummaryFinding(summary) {
    return scopedSummaryFindingModel(summary, engineReviewModelContext());
  }

  function engineSeverityRank(className) {
    return engineSeverityRankModel(className);
  }

  function engineMetricCylinder(key, metric) {
    return engineMetricCylinderModel(key, metric);
  }

  function engineReviewItems(summary) {
    return engineReviewItemsModel(summary, engineReviewModelContext());
  }

  function engineMetricLesson(key, metric = {}, finding = {}) {
    return engineMetricLessonModel(key, metric, finding);
  }

  function engineLessonHtml(lesson, options = {}) {
    if (!lesson) return "";
    const compact = options.compact ? " is-compact" : "";
    return `
      <div class="engine-lesson${compact}">
        <div>
          <span>What it watches</span>
          <p>${escapeHtml(lesson.watches)}</p>
        </div>
        <div>
          <span>What it means</span>
          <p>${escapeHtml(lesson.meaning)}</p>
        </div>
        <div>
          <span>How to review it</span>
          <p>${escapeHtml(lesson.review)}</p>
        </div>
      </div>
    `;
  }

  function engineFindingSnippet(value, maxLength = 190) {
    return engineFindingSnippetModel(value, maxLength);
  }

  function engineWindowLabel(windows) {
    return engineWindowLabelModel(windows);
  }

  function engineReviewActionList(reviewItems, fallbackFinding) {
    return engineReviewActionListModel(reviewItems, fallbackFinding);
  }

  function engineTriageStatus(reviewItems, summary) {
    return engineTriageStatusModel(reviewItems, summary);
  }

  function renderEngineTriageSummary(summary, reviewItems) {
    const container = document.querySelector("#engineTriageSummary");
    if (!container) return;
    const status = engineTriageStatus(reviewItems, summary);
    const primaryItem = reviewItems[0] ?? null;
    const primaryFinding = primaryItem?.finding ?? scopedSummaryFinding(summary);
    const primaryMetric = primaryItem?.metric ?? summary.overall;
    const scoreValue = Number.isFinite(primaryMetric?.score) ? primaryMetric.score : summary.overall.score;
    const scoreClass = primaryItem?.className ?? summary.overall.className;
    const scoreLabelText = primaryItem?.label ?? "Overall";
    const actions = engineReviewActionList(primaryItem ? [primaryItem] : [], primaryFinding);
    const meta = primaryFinding?.meta ?? primaryMetric?.note ?? "No active review cue";
    const lesson = engineMetricLesson(primaryItem?.key ?? "overall", primaryMetric, primaryFinding);
    container.className = `engine-triage-summary status-${status.className}`;
    if (primaryItem?.key) {
      container.dataset.engineReviewCard = primaryItem.key;
    } else {
      delete container.dataset.engineReviewCard;
    }
    container.innerHTML = `
      <header>
        <div>
          <span class="engine-status ${status.className}">${escapeHtml(status.label)}</span>
          <h3>${escapeHtml(primaryFinding?.title ?? "Engine review clear")}</h3>
        </div>
        <div class="engine-triage-score">
          <strong class="${scoreClass}">${Number.isFinite(scoreValue) ? scoreValue : "--"}</strong>
          <span>/100</span>
          <small>${escapeHtml(scoreLabelText)}</small>
        </div>
      </header>
      <p class="engine-triage-meta">${escapeHtml(meta)}</p>
      <p>${escapeHtml(engineFindingSnippet(primaryFinding?.body, 260))}</p>
      <div class="engine-lesson-heading">${escapeHtml(lesson.title)}</div>
      ${engineLessonHtml(lesson)}
      <ul class="engine-next-actions">
        ${actions.map((item) => `<li><span>${escapeHtml(item.source)}</span>${escapeHtml(item.text)}</li>`).join("")}
      </ul>
    `;
  }

  function renderEngineReviewList(reviewItems, summary) {
    const container = document.querySelector("#engineReviewList");
    if (!container) return;
    const queueItems = reviewItems.slice(1, 6);
    if (!reviewItems.length) {
      const fallbackFinding = scopedSummaryFinding(summary);
      const lesson = engineMetricLesson("overall", summary.overall, fallbackFinding);
      const actions = engineReviewActionList([], fallbackFinding);
      container.innerHTML = `
        <article class="engine-triage-summary engine-review-card status-${summary.overall.className} is-clear">
          <header>
            <div>
              <span class="engine-status ${summary.overall.className}">${escapeHtml(summary.overall.label)}</span>
              <h3>${Number.isFinite(summary.overall.score) ? "No active engine review cues" : "Not enough data for an overall review"}</h3>
            </div>
            <div class="engine-triage-score">
              <strong class="${summary.overall.className}">${Number.isFinite(summary.overall.score) ? summary.overall.score : "--"}</strong>
              <span>/100</span>
              <small>Overall</small>
            </div>
          </header>
          <p class="engine-triage-meta">${Number.isFinite(summary.overall.score) ? "No active review cue" : "No evaluated checks"}</p>
          <p>${escapeHtml(engineFindingSnippet(fallbackFinding?.body, 230))}</p>
          <div class="engine-lesson-heading">${escapeHtml(lesson.title)}</div>
          ${engineLessonHtml(lesson)}
          <ul class="engine-next-actions">
            ${actions.map((item) => `<li><span>${escapeHtml(item.source)}</span>${escapeHtml(item.text)}</li>`).join("")}
          </ul>
        </article>
      `;
      return;
    }
    if (!queueItems.length) {
      const lesson = engineMetricLesson("overall", summary.overall, summary.finding);
      container.innerHTML = `
        <article class="engine-triage-summary engine-review-card status-green is-clear">
          <header>
            <div>
              <span class="engine-status green">Queue clear</span>
              <h3>No additional review cues</h3>
            </div>
            <div class="engine-triage-score">
              <strong class="${summary.overall.className}">${Number.isFinite(summary.overall.score) ? summary.overall.score : "--"}</strong>
              <span>/100</span>
              <small>Overall</small>
            </div>
          </header>
          <p class="engine-triage-meta">The primary finding above is the only active engine cue at this sensitivity setting.</p>
          <p>Use the primary finding as the review focus for this log.</p>
          <div class="engine-lesson-heading">${escapeHtml(lesson.title)}</div>
          ${engineLessonHtml(lesson)}
        </article>
      `;
      return;
    }
    container.innerHTML = queueItems.map((item) => {
      const score = Number.isFinite(item.metric.score) ? item.metric.score : "--";
      const actions = engineReviewActionList([item], item.finding);
      const lesson = engineMetricLesson(item.key, item.metric, item.finding);
      const statusLabel = findingDisplayLabel(item.finding, item.metric);
      return `
        <article class="engine-triage-summary engine-review-card status-${item.className} ${item.state}" data-engine-review-card="${item.key}">
          <header>
            <div>
              <span class="engine-status ${item.className}">${escapeHtml(statusLabel)}</span>
              <h3>${escapeHtml(item.finding?.title ?? item.label)}</h3>
            </div>
            <div class="engine-triage-score">
              <strong class="${item.className}">${score}</strong>
              <span>/100</span>
              <small>${escapeHtml(item.label)}</small>
            </div>
          </header>
          <p class="engine-triage-meta">${findingMetaMarkup(item.finding, compactHealthNote(item.key, item.metric) || item.metric.note || "")}</p>
          <p>${escapeHtml(engineFindingSnippet(item.finding?.body))}</p>
          <div class="engine-lesson-heading">${escapeHtml(lesson.title)}</div>
          ${engineLessonHtml(lesson)}
          <ul class="engine-next-actions">
            ${actions.map((action) => `<li><span>${escapeHtml(action.source)}</span>${escapeHtml(action.text)}</li>`).join("")}
          </ul>
        </article>
      `;
    }).join("");
  }

  function renderEngineEvidencePanel(reviewItems) {
    const container = document.querySelector("#engineEventTimeline");
    if (!container) return;
    const events = reviewItems
      .flatMap((item) => item.windows.map((window) => ({ ...item, window })))
      .filter((event) => Number.isFinite(event.window.startTime))
      .sort((a, b) => a.window.startTime - b.window.startTime)
      .slice(0, 8);

    if (!events.length) {
      container.hidden = true;
      container.innerHTML = "";
      return;
    }
    container.hidden = false;

    const eventList = events.map((event) => {
      const windowLabel = event.window.label ?? engineWindowLabel([event.window]);
      const note = compactHealthNote(event.key, event.metric) || event.finding?.meta || event.metric?.note || "";
      return `<button class="engine-event-chip ${event.className}" data-engine-event-key="${escapeHtml(event.key)}" data-engine-event-title="${escapeHtml(event.label)}" data-engine-event-window="${escapeHtml(windowLabel)}" data-engine-event-note="${escapeHtml(note)}" aria-pressed="false" type="button"><span>${escapeHtml(event.label)}</span>${escapeHtml(windowLabel)}</button>`;
    }).join("");

    container.innerHTML = `
      <div class="engine-timeline-heading">
        <span>Review events</span>
        <strong>${events.length ? `${events.length} marker${events.length === 1 ? "" : "s"}` : "Clear"}</strong>
      </div>
      <div class="engine-timeline-events">${eventList}</div>
      <div class="engine-event-selection" id="engineEventSelection" hidden></div>
    `;
  }

  function renderEngineSupportingChecks(rows, reviewItems) {
    const diagnostics = document.querySelector("#engineDiagnosticGrid");
    const summaryElement = document.querySelector("#engineSupportingSummary");
    if (!diagnostics) return;
    const reviewKeys = new Set(reviewItems.map((item) => item.key));
    const supportingRows = rows.filter((row) => !row.isMonitorDisabled && !reviewKeys.has(row.key));
    if (summaryElement) {
      summaryElement.textContent = `Supporting checks (${supportingRows.length})`;
    }
    diagnostics.innerHTML = supportingRows.map((row) => {
      const score = Number.isFinite(row.metric.score) ? row.metric.score : "--";
      const lesson = engineMetricLesson(row.key, row.metric, row.finding);
      return `
        <article class="engine-diagnostic-card is-compact ${row.state}">
          <header>
            <div>
              <h3>${escapeHtml(row.label)}</h3>
              <p>${escapeHtml(row.description)}</p>
            </div>
            <strong class="${row.className}">${score}</strong>
          </header>
          <p><b>${escapeHtml(findingDisplayLabel(row.finding, row.metric))}</b> ${escapeHtml(compactHealthNote(row.key, row.metric) || row.finding?.meta || "No review note available")}</p>
          <p class="engine-supporting-lesson">${escapeHtml(lesson.watches)}</p>
        </article>
      `;
    }).join("");
  }

  function selectEngineTimelineEvidence(button) {
    const key = button?.dataset?.engineEventKey;
    if (!key) return;
    document.querySelectorAll("[data-engine-event-key]").forEach((eventButton) => {
      const isSelected = eventButton.dataset.engineEventKey === key;
      eventButton.classList.toggle("is-selected", isSelected);
      eventButton.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
    document.querySelectorAll("[data-engine-review-card]").forEach((card) => {
      card.classList.toggle("is-evidence-selected", card.dataset.engineReviewCard === key);
    });
    const detail = document.querySelector("#engineEventSelection");
    if (!detail) return;
    const title = button.dataset.engineEventTitle ?? "Review cue";
    const windowLabel = button.dataset.engineEventWindow ?? "";
    const note = button.dataset.engineEventNote ?? "";
    detail.hidden = false;
    detail.innerHTML = `
      <span>${escapeHtml(title)}</span>
      <strong>${escapeHtml(windowLabel)}</strong>
      ${note ? `<p>${escapeHtml(note)}</p>` : ""}
    `;
  }

  return {
    engineReviewModelContext,
    engineMetricRows,
    engineForSummaryScope,
    metricMatchesEngineScope,
    scopedSummaryFinding,
    engineSeverityRank,
    engineMetricCylinder,
    engineReviewItems,
    engineMetricLesson,
    engineLessonHtml,
    engineFindingSnippet,
    engineWindowLabel,
    engineReviewActionList,
    engineTriageStatus,
    renderEngineTriageSummary,
    renderEngineReviewList,
    renderEngineEvidencePanel,
    renderEngineSupportingChecks,
    selectEngineTimelineEvidence,
  };
}
